import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isNative } from '../api/origin.ts';

/**
 * Android's hardware back, inside the shell only. Capacitor's default answer
 * to the button is to close the activity — from three screens deep in
 * settings as readily as from the list. With a listener registered, back
 * means what it means in a browser: one step through history, out of the
 * conversation or a settings screen. Only the chat list minimizes the app —
 * minimize rather than exit, so the socket and the loaded threads are still
 * warm when the icon is tapped again.
 *
 * The list is recognised two ways because it is reached two ways: by path,
 * since the in-app «Назад» *pushes* '/' rather than popping to it, and by a
 * history index of zero, which also covers /login and any other cold start —
 * a press with nothing behind it must leave the app, not sit dead.
 */
export function useNativeBack(): void {
  const location = useLocation();
  const atListRef = useRef(false);
  atListRef.current = location.pathname === '/';

  useEffect(() => {
    if (!isNative) return;

    // Loaded on demand so the web bundle never fetches the plugin: `isNative`
    // is decided at build time, but the bundler cannot see that through the
    // module boundary.
    let disposed = false;
    let handle: { remove: () => Promise<void> } | null = null;
    void import('@capacitor/app').then(async ({ App }) => {
      const listener = await App.addListener('backButton', () => {
        const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (atListRef.current || idx === 0) void App.minimizeApp();
        else window.history.back();
      });
      if (disposed) void listener.remove();
      else handle = listener;
    });
    return () => {
      disposed = true;
      void handle?.remove();
    };
  }, []);
}
